import React from 'react';
import { Form, Input, Button, Card, message } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authApi } from '@/features/auth/api/auth-api';
import { useAuthStore } from "@/store/auth-store";
import { LanguageToggle } from '@/components/common/LanguageToggle';

interface LoginFormValues {
    username: string;
    password: string;
}

export const LoginPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const setAuth = useAuthStore((state) => state.setAuth);
    const [loading, setLoading] = React.useState(false);

    const onFinish = async (values: LoginFormValues) => {
        setLoading(true);
        try {
            const res = await authApi.login(values);
            if (res.code === 200) {
                message.success(t('auth.login_success'));
                setAuth(res.data.token, {
                    username: res.data.username,
                    roles: res.data.roles,
                    permissions: res.data.permissions,
                    platformAdmin: res.data.platformAdmin ?? false,
                });
                navigate('/');
            } else {
                message.error(res.message || t('auth.login_failed'));
            }
        } catch (error) {
            message.error(t('auth.login_failed'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-100 px-4 relative">
            <div className="absolute top-4 right-4">
                <LanguageToggle />
            </div>
            <Card title={t('auth.login_title')} className="w-full max-w-96 shadow-lg">
                <Form
                    name="login_form"
                    initialValues={{ remember: true }}
                    onFinish={onFinish}
                    size="large"
                >
                    <Form.Item
                        name="username"
                        rules={[{ required: true, message: t('auth.username_required') }]}
                    >
                        <Input prefix={<UserOutlined />} placeholder={t('auth.username')} />
                    </Form.Item>
                    <Form.Item
                        name="password"
                        rules={[{ required: true, message: t('auth.password_required') }]}
                    >
                        <Input.Password prefix={<LockOutlined />} placeholder={t('auth.password')} />
                    </Form.Item>

                    <Form.Item>
                        <Button type="primary" htmlType="submit" className="w-full" loading={loading}>
                            {t('auth.login_button')}
                        </Button>
                    </Form.Item>

                    <div className="text-center text-sm text-gray-500">
                        {t('auth.no_account')}{' '}
                        <Link to="/register">{t('auth.go_to_register')}</Link>
                    </div>
                </Form>
            </Card>
        </div>
    );
};
